import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';

const navItems = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About us' },
  { id: 'services', label: 'Services' },
  { id: 'testimonials', label: 'Testimonials' },
  { id: 'contact', label: 'Contact' },
];

const services = [
  {
    icon: 'fa-solid fa-car-burst',
    title: 'Windscreen/Rear screen replacement',
    description: 'We provide professional front and rear windscreen replacement for cars, bakkies, SUVs, taxis, fleet vehicles, minibuses, and commercial vehicles across Cape Town and the Western Cape. Our technicians remove damaged glass carefully, prepare the bonding surface, fit the correct replacement windscreen or rear glass, seal it properly, check for leaks, and hand the vehicle back clean and road-ready.',
    images: [
      '/images/work/windscreen-glass-positioning.jpeg',
      '/images/work/rear-windscreen-service.jpeg',
    ],
  },
  {
    icon: 'fa-solid fa-truck-fast',
    title: 'Mobile Call-Outs',
    description: 'We offer mobile auto glass services across all areas of the Western Cape, including Cape Town, Bellville, Brackenfell, Durbanville, Parow, Goodwood, Milnerton, Table View, Bloubergstrand, Somerset West, Strand, Gordon\'s Bay, Stellenbosch, Paarl, Wellington, Malmesbury, Atlantis, Worcester, Hermanus, George, Mossel Bay, Knysna, Plettenberg Bay, Oudtshoorn, and surrounding areas. Call-out fees start from R150 and vary depending on your location.',
    image: '/images/work/mobile-callout-yellow-car.jpeg',
    badge: 'From R150',
  },
  {
    icon: 'fa-solid fa-car-side',
    title: 'Mirror Service',
    description: 'We supply and fit side mirror glass, mirror backing plates, and selected mirror housing parts for many cars, bakkies, SUVs, taxis, and fleet vehicles. Whether your mirror was cracked, stolen, knocked loose, or damaged in a parking accident, we help identify the correct left or right mirror glass and complete a neat, secure replacement so your visibility is restored.',
    image: '/images/work/mirror-service.jpeg',
  },
  {
    icon: 'fa-solid fa-window-restore',
    title: 'Door Glass',
    description: 'We replace damaged door glass for front and rear vehicle doors, including glass broken during attempted theft, accidents, impact damage, or regulator repairs. Our team removes loose glass from the door cavity, checks the window channel, fits the correct replacement panel, and tests the movement so the window opens and closes properly.',
    image: '/images/work/door-glass-service.jpeg',
  },
  {
    icon: 'fa-solid fa-screwdriver-wrench',
    title: 'Remove and Refit',
    description: 'Our remove and refit service is ideal when a windscreen or fixed vehicle glass needs to come out safely for body repairs, rust treatment, panel beating, spray painting, leak repairs, or resealing. We carefully remove the existing glass where possible, clean and prepare the aperture, then refit and seal it correctly so the vehicle leaves neat, secure, and protected.',
    image: '/images/work/remove-and-refit-service.jpeg',
  },
  {
    icon: 'fa-solid fa-window-maximize',
    title: 'Vent Glass',
    description: 'We supply and fit vent glass, quarter glass, and small fixed side windows for many vehicle makes and models, including SUVs, bakkies, sedans, and commercial vehicles. Whether the glass was damaged during an attempted break-in, accident, or body repair, we help identify the correct panel, remove broken glass safely, prepare the frame, and complete a neat, secure fitment.',
    image: '/images/work/vent-glass-examples.jpeg',
  },
];

const testimonials = [
  {
    name: 'Thabo M.',
    role: 'Business owner',
    text: 'They replaced my windscreen at work and kept everything neat. Fast communication, fair pricing, and a clean fit.',
  },
  {
    name: 'Sarah V.',
    role: 'Family vehicle owner',
    text: 'The team explained the process clearly and helped me choose the right glass. The car was ready the same day.',
  },
  {
    name: 'David N.',
    role: 'E-hailing driver',
    text: 'I needed my car back on the road quickly. Excellent Auto Glass handled the door glass without wasting my day.',
  },
];

const galleryImages = [
  { src: '/gallery/safe/gallery-01.jpeg', alt: 'Auto glass fitment workshop with windscreen prepared on stand' },
  { src: '/gallery/safe/gallery-02.jpeg', alt: 'Red Jeep windscreen replacement service' },
  { src: '/gallery/safe/gallery-03.jpeg', alt: 'Vehicle glass replacement on grey SUV outside workshop' },
  { src: '/gallery/safe/gallery-04.jpeg', alt: 'Mobile auto glass service with vehicles on site' },
  { src: '/gallery/safe/gallery-05.jpeg', alt: 'BMW auto glass work in Excellent Auto Glass workshop' },
  { src: '/gallery/safe/gallery-06.jpeg', alt: 'Blue BMW windscreen fitment with suction tools' },
  { src: '/gallery/safe/gallery-07.jpeg', alt: 'Green Mercedes windscreen fitment on site' },
  { src: '/gallery/safe/gallery-08.jpeg', alt: 'Mercedes windscreen replacement preparation in workshop' },
  { src: '/gallery/safe/gallery-09.jpeg', alt: 'Jeep glass fitment beside Excellent Auto Glass mobile vehicle' },
  { src: '/gallery/safe/gallery-10.jpeg', alt: 'Truck windscreen replacement with mobile auto glass vehicle' },
  { src: '/gallery/safe/gallery-11.jpeg', alt: 'Green vehicle windscreen service with mobile auto glass call-out' },
  { src: '/gallery/safe/gallery-12.jpeg', alt: 'Workshop vehicle glass service with Excellent Auto Glass bakkie' },
  { src: '/gallery/safe/gallery-13.jpeg', alt: 'Mercedes vehicle glass service with replacement windscreen on stand' },
  { src: '/gallery/safe/gallery-14.jpeg', alt: 'Mercedes windscreen work with replacement glass prepared' },
  { src: '/gallery/safe/gallery-15.jpeg', alt: 'Blue BMW vehicle glass service outside workshop' },
  { src: '/gallery/safe/gallery-16.jpeg', alt: 'Toyota windscreen fitment outside auto glass workshop' },
  { src: '/gallery/safe/gallery-17.jpeg', alt: 'Toyota windscreen service in Excellent Auto Glass workshop' },
  { src: '/gallery/safe/gallery-18.jpeg', alt: 'Truck windscreen replacement with glass prepared for fitment' },
  { src: '/gallery/safe/gallery-19.jpeg', alt: 'Mercedes windscreen replacement with new glass prepared on a stand' },
  { src: '/gallery/safe/gallery-20.jpeg', alt: 'Land Rover Discovery windscreen replacement in a parking garage' },
  { src: '/gallery/safe/gallery-21.jpeg', alt: 'Porsche and Range Rover auto glass service at a customer workshop' },
  { src: '/gallery/safe/gallery-22.jpeg', alt: 'Porsche windscreen replacement with auto glass tools on site' },
  { src: '/gallery/safe/gallery-23.jpeg', alt: 'Completed Porsche windscreen fitment in the workshop' },
];

const processSteps = [
  'Send your vehicle details and photos',
  'We confirm glass availability and pricing',
  'Book workshop fitment or a mobile call-out',
  'We install, inspect, clean, and hand over',
];

function App() {
  const heroVideoRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [fileName, setFileName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleName: '',
    vehicleModel: '',
    yearModel: '',
    vinNumber: '',
    rainSensor: false,
    camera: false,
    service: '',
    message: '',
  });
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.16 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // Mobile browsers may block autoplay in low-power or data-saver modes.
      });
    }
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({ top: element.offsetTop - 78, behavior: 'smooth' });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(`Selected: ${file.name}`);
      setImageFile(file);
    } else {
      setFileName('');
      setImageFile(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    const url = process.env.NODE_ENV === 'development'
      ? 'http://localhost:8000/api/quote-request/'
      : 'https://api.excellentautoglass.co.za/api/quote-request/';

    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });
    setFieldErrors({});

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone_number', formData.phone);
      formDataToSend.append('vehicle_name', formData.vehicleName);
      formDataToSend.append('vehicle_model', formData.vehicleModel);
      formDataToSend.append('year_model', formData.yearModel);
      formDataToSend.append('vin_number', formData.vinNumber);
      formDataToSend.append('rain_sensor', formData.rainSensor ? 'yes' : 'no');
      formDataToSend.append('camera', formData.camera ? 'yes' : 'no');
      formDataToSend.append('service', formData.service);
      formDataToSend.append('message', formData.message);

      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }

      await axios.post(url, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSubmitMessage({
        type: 'success',
        text: 'Thank you. We received your request and will get back to you within 24 hours.',
      });
      setFormData({ name: '', email: '', phone: '', vehicleName: '', vehicleModel: '', yearModel: '', vinNumber: '', rainSensor: false, camera: false, service: '', message: '' });
      setFileName('');
      setImageFile(null);

      const fileInput = document.getElementById('image');
      if (fileInput) fileInput.value = '';

      setTimeout(() => setSubmitMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        if (typeof errorData === 'object' && !errorData.message) {
          setFieldErrors(errorData);
          setSubmitMessage({ type: 'error', text: 'Please correct the highlighted fields and try again.' });
        } else {
          setSubmitMessage({
            type: 'error',
            text: errorData.message || 'Something went wrong. Please try again later.',
          });
        }
      } else {
        setSubmitMessage({
          type: 'error',
          text: 'Unable to connect to the server. Please check your connection and try again.',
        });
      }

      setTimeout(() => setSubmitMessage({ type: '', text: '' }), 7000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-shell">
          <button className="brand" onClick={() => scrollToSection('home')} aria-label="Go to home">
            <img src="/logo-cropped.png" alt="Excellent Auto Glass" />
          </button>

          <ul className={`nav-links ${mobileMenuOpen ? 'active' : ''}`}>
            {navItems.map((item) => (
              <li key={item.id}>
                <button onClick={() => { scrollToSection(item.id); setMobileMenuOpen(false); }}>
                  {item.label}
                </button>
              </li>
            ))}
          </ul>

          <button className="nav-cta" onClick={() => scrollToSection('contact')}>
            <i className="fa-solid fa-paper-plane"></i>
            Quote
          </button>

          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'}`}></i>
          </button>
        </div>
      </nav>

      <section id="home" className="hero">
        <div className="hero-media">
          <video ref={heroVideoRef} autoPlay muted loop playsInline preload="auto" poster="/images/work/windscreen-glass-positioning.jpeg">
            <source src="/videos/hero-video-171400.mp4?v=20260705-171400" type="video/mp4" />
          </video>
          <img src="/images/work/windscreen-glass-positioning.jpeg" alt="Auto glass windscreen fitment" />
        </div>
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <div className="hero-copy animate-on-scroll">
            <span className="eyebrow">Cape Town auto glass specialists</span>
            <h1>Excellent Auto Glass</h1>
            <p>
              Mobile and workshop windscreen replacement, rear windscreens, door glass, mirror glass, remove and refit,
              and careful fitment for everyday drivers, fleets, and workshops.
            </p>
            <div className="hero-actions">
              <button className="btn btn-primary" onClick={() => scrollToSection('contact')}>
                <i className="fa-solid fa-file-signature"></i>
                Request a quote
              </button>
              <a className="btn btn-ghost" href="https://wa.link/1nfdd4">
                <i className="fa-brands fa-whatsapp"></i>
                WhatsApp us
              </a>
            </div>
          </div>

          <div className="hero-panel animate-on-scroll delay-1">
            <p>Call-out fee starts from</p>
            <strong>R150</strong>
            <span>Final pricing depends on location, vehicle, and glass availability.</span>
          </div>
        </div>
      </section>

      <section id="about" className="section about">
        <div className="container split-grid">
          <div className="section-copy animate-on-scroll">
            <span className="eyebrow">About us</span>
            <h2>Professional auto glass fitment with reliable service from start to finish.</h2>
            <p>
              Excellent Auto Glass helps drivers and businesses replace damaged vehicle glass quickly
              without cutting corners. With more than 10 years of auto glass experience, our team handles
              preparation, bonding, sealing, and clean-up with the same discipline whether you visit us in
              Parow or book a mobile call-out.
            </p>
            <div className="feature-list">
              <div>
                <i className="fa-solid fa-circle-check"></i>
                More than 10 years of practical auto glass experience
              </div>
              <div>
                <i className="fa-solid fa-circle-check"></i>
                6-month warranty on workmanship for peace of mind
              </div>
              <div>
                <i className="fa-solid fa-circle-check"></i>
                Shatterprufe glass options and SABS-approved standards
              </div>
            </div>
            <div className="quality-badges" aria-label="Excellent Auto Glass quality standards">
              <div>
                <strong>10+ years</strong>
                <span>Auto glass experience</span>
              </div>
              <div>
                <strong>6 months</strong>
                <span>Workmanship warranty</span>
              </div>
              <div className="sabs-badge-card">
                <img src="/images/sabs-approved.svg" alt="SABS Approved" />
                <span>SABS-approved standards</span>
              </div>
              <div>
                <strong>Shatterprufe</strong>
                <span>Trusted glass options</span>
              </div>
            </div>
          </div>
          <div className="about-stack animate-on-scroll delay-1">
            <img className="about-main" src="/images/work/windscreen-bmw-lift.jpeg" alt="Technicians lifting a replacement windscreen" />
            <img className="about-card" src="/images/work/windscreen-prep-white-suv.jpeg" alt="Preparing a vehicle for windscreen fitment" />
          </div>
        </div>
      </section>

      <section id="services" className="section services">
        <div className="container">
          <div className="section-heading animate-on-scroll">
            <span className="eyebrow">Services</span>
            <h2>Everything your vehicle glass needs, handled in one place.</h2>
            <p>
              We supply and fit quality auto glass for private vehicles, fleets, workshops, and panel shops
              across Cape Town and the Western Cape. From sourcing the correct windscreen, rear glass, door
              glass, mirror glass, vent glass, quarter glass, fixed side window, or remove-and-refit work to professional fitment, sealing, inspection, and clean
              handover, our team manages the complete vehicle glass replacement process.
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <article className={`service-card animate-on-scroll delay-${index % 3}`} key={service.title}>
                <div className={`service-image ${service.images ? 'service-image-duo' : ''} ${['Mobile Call-Outs', 'Vent Glass'].includes(service.title) ? 'service-image-contain' : ''}`}>
                  {service.images ? (
                    service.images.map((image, imageIndex) => (
                      <img src={image} alt={`${service.title} ${imageIndex + 1}`} key={image} />
                    ))
                  ) : (
                    <img src={service.image} alt={service.title} />
                  )}
                  {service.badge && <span>{service.badge}</span>}
                </div>
                <div className="service-body">
                  <i className={service.icon}></i>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section process">
        <div className="container process-grid">
          <div className="section-copy animate-on-scroll">
            <span className="eyebrow">How it works</span>
            <h2>A simple booking flow that gets you back on the road.</h2>
          </div>
          <div className="steps">
            {processSteps.map((step, index) => (
              <div className="step animate-on-scroll" key={step}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <p>{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section seo-content">
        <div className="container">
          <div className="seo-panel animate-on-scroll">
            <span className="eyebrow">Cape Town service area</span>
            <h2>Auto glass replacement, windscreen fitment, and mobile glass call-outs in Cape Town.</h2>
            <p>
              Excellent Auto Glass provides professional car glass replacement for private vehicles,
              business fleets, e-hailing cars, panel shops, and workshops across Cape Town. Our services
              include front windscreen replacement, rear windscreen replacement, door glass replacement,
              mirror glass replacement, remove and refit, vent glass, quarter glass, fixed side windows, glass supply, and complete supply-and-fitment
              solutions.
            </p>
            <p>
              If you are searching for windscreen replacement near me, mobile auto glass Cape Town,
              car window replacement, mirror glass replacement, rear windscreen fitment, remove and refit service, or a quick quote for vehicle glass,
              our team can help confirm the correct glass, advise on availability, and arrange workshop
              fitment or a mobile call-out from R150.
            </p>
            <div className="seo-keywords" aria-label="Popular auto glass services">
              <span>Windscreen replacement Cape Town</span>
              <span>Mobile auto glass call-outs</span>
              <span>Car window replacement</span>
              <span>Mirror glass replacement</span>
              <span>Rear windscreen fitment</span>
              <span>Door glass replacement</span>
              <span>Remove and refit</span>
              <span>Vent glass replacement</span>
              <span>Vehicle glass supply</span>
              <span>Supply and fitment</span>
              <span>Auto glass quote</span>
            </div>
          </div>
        </div>
      </section>

      <section className="section gallery">
        <div className="container">
          <div className="gallery-heading animate-on-scroll">
            <div>
              <span className="eyebrow">Work gallery</span>
              <h2>Scroll through recent auto glass work.</h2>
            </div>
          </div>
          <div className="gallery-window" aria-label="Recent auto glass work">
            <div className={`gallery-track ${selectedGalleryImage ? 'is-paused' : ''}`}>
              {[...galleryImages, ...galleryImages].map((image, index) => (
                <article className="gallery-card" key={`${image.src}-${index}`}>
                  <button type="button" onClick={() => setSelectedGalleryImage(image)} aria-label="View full gallery image">
                    <img src={image.src} alt={image.alt} />
                  </button>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="testimonials" className="section testimonials">
        <div className="container">
          <div className="section-heading animate-on-scroll">
            <span className="eyebrow">Testimonials</span>
            <h2>Trusted by drivers who need reliable glass work.</h2>
          </div>
          <div className="testimonial-grid">
            {testimonials.map((testimonial, index) => (
              <article className={`testimonial-card animate-on-scroll delay-${index}`} key={testimonial.name}>
                <div className="stars" aria-label="5 star rating">
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                </div>
                <p>{testimonial.text}</p>
                <div>
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.role}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="section contact">
        <div className="container contact-grid">
          <div className="contact-copy animate-on-scroll">
            <span className="eyebrow">Contact us</span>
            <h2>Request a quote or book a call-out.</h2>
            <p>
              Upload a photo if you have one. It helps us confirm the right glass faster.
              Call-out fee starts from R150.
            </p>

            <div className="contact-cards">
              <a href="tel:+27658681627">
                <i className="fa-solid fa-phone"></i>
                <span>+27 65 868 1627</span>
              </a>
              <a href="mailto:info@excellentautoglass.co.za">
                <i className="fa-solid fa-envelope"></i>
                <span>info@excellentautoglass.co.za</span>
              </a>
              <div>
                <i className="fa-solid fa-location-dot"></i>
                <span>63 King Edward Street, Parow, Cape Town</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="quote-form animate-on-scroll delay-1">
            <div className="fee-note">
              <i className="fa-solid fa-circle-info"></i>
              Call-out fee starts from R150.
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full name *</label>
                <input id="name" name="name" type="text" value={formData.name} onChange={handleInputChange} required disabled={isSubmitting} className={fieldErrors.name ? 'error' : ''} />
                {fieldErrors.name && <FieldError error={fieldErrors.name} />}
              </div>
              <div className="form-group">
                <label htmlFor="email">Email address *</label>
                <input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required disabled={isSubmitting} className={fieldErrors.email ? 'error' : ''} />
                {fieldErrors.email && <FieldError error={fieldErrors.email} />}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone number *</label>
                <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required disabled={isSubmitting} placeholder="+27 65 868 1627" className={fieldErrors.phone_number ? 'error' : ''} />
                {fieldErrors.phone_number && <FieldError error={fieldErrors.phone_number} />}
              </div>
              <div className="form-group">
                <label htmlFor="vehicleName">Vehicle name</label>
                <input id="vehicleName" name="vehicleName" type="text" value={formData.vehicleName} onChange={handleInputChange} disabled={isSubmitting} placeholder="e.g. Toyota Hilux 2021" className={fieldErrors.vehicle_name ? 'error' : ''} />
                {fieldErrors.vehicle_name && <FieldError error={fieldErrors.vehicle_name} />}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="vinNumber">VIN number</label>
                <input id="vinNumber" name="vinNumber" type="text" value={formData.vinNumber} onChange={handleInputChange} disabled={isSubmitting} placeholder="Vehicle identification number" className={fieldErrors.vin_number ? 'error' : ''} />
                {fieldErrors.vin_number && <FieldError error={fieldErrors.vin_number} />}
              </div>
              <div className="form-group">
                <label htmlFor="vehicleModel">Vehicle model</label>
                <input id="vehicleModel" name="vehicleModel" type="text" value={formData.vehicleModel} onChange={handleInputChange} disabled={isSubmitting} placeholder="e.g. Hilux, Polo, X5" className={fieldErrors.vehicle_model ? 'error' : ''} />
                {fieldErrors.vehicle_model && <FieldError error={fieldErrors.vehicle_model} />}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="yearModel">Year model</label>
                <input id="yearModel" name="yearModel" type="text" value={formData.yearModel} onChange={handleInputChange} disabled={isSubmitting} placeholder="e.g. 2021" className={fieldErrors.year_model ? 'error' : ''} />
                {fieldErrors.year_model && <FieldError error={fieldErrors.year_model} />}
              </div>
              <div className="form-group">
                <label htmlFor="service">Service needed</label>
                <select id="service" name="service" value={formData.service} onChange={handleInputChange} disabled={isSubmitting} className={fieldErrors.service ? 'error' : ''}>
                  <option value="">Select a service</option>
                  {services.map((service) => (
                    <option value={service.title} key={service.title}>{service.title}</option>
                  ))}
                </select>
                {fieldErrors.service && <FieldError error={fieldErrors.service} />}
              </div>
            </div>

            <div className="checkbox-group" aria-label="Vehicle glass features">
              <label>
                <input type="checkbox" name="rainSensor" checked={formData.rainSensor} onChange={handleInputChange} disabled={isSubmitting} />
                <span>Rain sensor</span>
              </label>
              <label>
                <input type="checkbox" name="camera" checked={formData.camera} onChange={handleInputChange} disabled={isSubmitting} />
                <span>Camera</span>
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea id="message" name="message" rows="5" value={formData.message} onChange={handleInputChange} required disabled={isSubmitting} placeholder="Tell us your vehicle make/model, glass needed, and preferred location." className={fieldErrors.message ? 'error' : ''}></textarea>
              {fieldErrors.message && <FieldError error={fieldErrors.message} />}
            </div>

            <div className="form-group">
              <label htmlFor="image" className="file-label">
                <i className="fa-solid fa-cloud-arrow-up"></i>
                <span>Upload image optional</span>
                <input id="image" name="image" type="file" accept="image/*" onChange={handleFileChange} disabled={isSubmitting} />
              </label>
              {fileName && <div className="file-name">{fileName}</div>}
              {fieldErrors.image && <FieldError error={fieldErrors.image} />}
            </div>

            <button type="submit" className="btn btn-primary btn-submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Sending
                </>
              ) : (
                <>
                  Send request
                  <i className="fa-solid fa-arrow-right"></i>
                </>
              )}
            </button>

            {submitMessage.text && (
              <div className={`form-message ${submitMessage.type}`}>
                <i className={`fa-solid ${submitMessage.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                <span>{submitMessage.text}</span>
              </div>
            )}
          </form>
        </div>
      </section>

      <footer className="footer">
        <div className="container footer-grid">
          <div>
            <h2 className="footer-brand">Excellent Auto Glass</h2>
            <p>Professional auto glass replacement, mobile call-outs, and fitment services in Cape Town.</p>
            <p className="footer-address"><i className="fa-solid fa-location-dot"></i> 63 King Edward Street, Parow, Cape Town</p>
          </div>
          <div className="footer-links">
            {navItems.map((item) => (
              <button key={item.id} onClick={() => scrollToSection(item.id)}>{item.label}</button>
            ))}
          </div>
          <div className="social-links">
            <a href="https://www.facebook.com/share/1FyZonkoie/?mibextid=wwXIfr" aria-label="Facebook"><i className="fa-brands fa-facebook-f"></i></a>
            <a href="https://www.instagram.com/excellent_auto_glass?igsh=ZXBtdzc5dzU2NWx1" aria-label="Instagram"><i className="fa-brands fa-instagram"></i></a>
            <a href="https://wa.link/1nfdd4" aria-label="WhatsApp"><i className="fa-brands fa-whatsapp"></i></a>
            <a href="https://www.tiktok.com/@excellentautoglass0?_r=1" aria-label="TikTok"><i className="fa-brands fa-tiktok"></i></a>
          </div>
        </div>
        <p className="footer-bottom">&copy; {new Date().getFullYear()} Excellent Auto Glass. All rights reserved.</p>
      </footer>

      {selectedGalleryImage && (
        <div className="image-modal" role="dialog" aria-modal="true" aria-label="Full gallery image" onClick={() => setSelectedGalleryImage(null)}>
          <button type="button" className="image-modal-close" onClick={() => setSelectedGalleryImage(null)} aria-label="Close image">
            <i className="fa-solid fa-xmark"></i>
          </button>
          <img src={selectedGalleryImage.src} alt={selectedGalleryImage.alt} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

function FieldError({ error }) {
  return (
    <div className="field-error">
      <i className="fa-solid fa-circle-exclamation"></i>
      {Array.isArray(error) ? error[0] : error}
    </div>
  );
}

export default App;
